import React, { useState } from 'react';
import { Sparkles, Eye, ShoppingBag, Star, Check } from 'lucide-react';
import { Product, NailShape } from '../types';
import { NailArtPreview } from './NailArtPreview';
import { useCart } from '../context/CartContext';

interface ProductCardProps {
  product: Product;
  onQuickView: (product: Product) => void;
}

export const ProductCard: React.FC<ProductCardProps> = ({ product, onQuickView }) => {
  const { addToCart } = useCart();
  const [selectedShape, setSelectedShape] = useState<NailShape>(product.defaultShape);
  const [justAdded, setJustAdded] = useState(false);

  const handleQuickAdd = (e: React.MouseEvent) => {
    e.stopPropagation();
    addToCart(product, selectedShape, product.defaultLength, 'S');
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 1800);
  };

  return (
    <div
      onClick={() => onQuickView(product)}
      className="group relative rounded-3xl bg-white border border-[#EADFCF] hover:border-[#D4AF37]/80 shadow-xs hover:shadow-xl transition-all duration-300 flex flex-col overflow-hidden cursor-pointer"
    >
      {/* Top Visual Artwork Container */}
      <div className="relative overflow-hidden bg-gradient-to-b from-[#FFFDF9] to-[#F5EFE6]">
        {/* Badges */}
        <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5">
          {product.isBestSeller && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FEF08A] border border-[#FDE047] text-[#854D0E] text-[10px] font-extrabold uppercase tracking-wider shadow-xs">
              <Sparkles className="w-3 h-3 text-[#D97706]" />
              Best Seller
            </span>
          )}
          {product.isNew && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#CCFBF1] border border-[#99F6E4] text-[#0F766E] text-[10px] font-extrabold uppercase tracking-wider shadow-xs">
              ✨ New Design
            </span>
          )}
        </div>

        {/* Price Tag Badge */}
        <div className="absolute top-3 right-3 z-10">
          <div className="bg-white/95 backdrop-blur-xs px-3 py-1 rounded-full shadow-md border border-[#F3C053]/40">
            <span className="font-serif font-extrabold text-sm sm:text-base text-[#B45309]">
              ${product.price.toFixed(2)}
            </span>
          </div>
        </div>

        {/* 3D Nail Preview Art */}
        <div className="transition-transform duration-500 group-hover:scale-105">
          <NailArtPreview
            productId={product.id}
            shape={selectedShape}
            className="w-full h-60 sm:h-64"
            showAllFive={false}
          />
        </div>

        {/* Quick View Hover Overlay Button */}
        <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
          <span className="bg-white/95 backdrop-blur-md text-[#292524] text-xs font-bold px-4 py-2 rounded-full shadow-lg border border-[#D4AF37]/30 flex items-center gap-1.5 transform translate-y-2 group-hover:translate-y-0 transition-transform">
            <Eye className="w-3.5 h-3.5 text-[#D4AF37]" />
            Customize Shape & Size
          </span>
        </div>
      </div>

      {/* Product Information */}
      <div className="p-5 flex-1 flex flex-col justify-between space-y-4">
        
        <div className="space-y-1.5">
          {/* Category & Star Rating */}
          <div className="flex items-center justify-between text-xs">
            <span className="text-[11px] font-semibold text-[#854D0E] uppercase tracking-wider">
              {product.categoryLabel}
            </span>
            <div className="flex items-center gap-1 text-[#F59E0B]">
              <Star className="w-3.5 h-3.5 fill-[#F59E0B]" />
              <span className="font-bold text-[#44403C] text-xs">{product.rating.toFixed(1)}</span>
              <span className="text-[#A8A29E] text-[11px]">({product.reviewCount})</span>
            </div>
          </div>

          {/* Title */}
          <h3 className="font-serif text-lg font-bold text-[#292524] group-hover:text-[#B45309] transition-colors line-clamp-1">
            {product.name}
          </h3>

          {/* Short description */}
          <p className="text-xs text-[#78716C] line-clamp-2 leading-relaxed">
            {product.shortDescription}
          </p>
        </div>

        {/* Shape Quick Switcher Pills */}
        <div className="space-y-1.5 pt-1 border-t border-[#F5EBE0]" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between text-[11px] text-[#78716C]">
            <span className="font-medium">Shape Preview:</span>
            <span className="font-semibold text-[#292524]">{selectedShape}</span>
          </div>
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
            {(['Almond', 'Coffin', 'Oval', 'Square', 'Stiletto'] as NailShape[]).map((shape) => (
              <button
                key={shape}
                type="button"
                onClick={() => setSelectedShape(shape)}
                className={`px-2 py-0.5 rounded-lg text-[10px] font-semibold transition-all shrink-0 ${
                  selectedShape === shape
                    ? 'bg-[#FEF08A] text-[#854D0E] border border-[#FDE047] shadow-xs'
                    : 'bg-[#FAF5EE] text-[#78716C] hover:bg-[#F3EBE0] border border-transparent'
                }`}
              >
                {shape}
              </button>
            ))}
          </div>
        </div>

        {/* Card Action Buttons */}
        <div className="pt-2 flex items-center gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onQuickView(product);
            }}
            className="flex-1 py-2.5 px-3 rounded-xl bg-[#FAF5EE] hover:bg-[#F5EFE6] text-[#44403C] text-xs font-bold border border-[#E7DEC8] hover:border-[#D4AF37] transition-all text-center"
          >
            Customize
          </button>

          <button
            type="button"
            onClick={handleQuickAdd}
            disabled={justAdded}
            className={`py-2.5 px-4 rounded-xl text-xs font-bold text-white transition-all flex items-center justify-center gap-1.5 shadow-sm ${
              justAdded
                ? 'bg-[#10B981] border border-[#059669]'
                : 'gold-shimmer-btn hover:scale-102 active:scale-98'
            }`}
          >
            {justAdded ? (
              <>
                <Check className="w-3.5 h-3.5" />
                <span>Added!</span>
              </>
            ) : (
              <>
                <ShoppingBag className="w-3.5 h-3.5" />
                <span>Quick Add</span>
              </>
            )}
          </button>
        </div>

      </div>
    </div>
  );
};
